import { DestroyRef, Directive, ElementRef, effect, inject, input, NgZone, Renderer2 } from '@angular/core';
import { BreakpointService } from 'src/app/services/breakpoint.service';

/**
 * Slides a toolbar out of the way while the user scrolls the list down and brings it back on the first scroll up
 * (or when the list is back at the top). Phones and tablets only (below 1024px); `appHideOnScroll` takes the
 * height the toolbar collapses by. It adds the `scroll-hidden` class and sets `--hide-on-scroll-offset`, so the
 * host decides how to collapse (see the components list).
 *
 * Scroll events do not bubble, so one capturing listener on the document picks up whichever list scrolls.
 */
@Directive({ selector: '[appHideOnScroll]', standalone: true })
export class HideOnScrollDirective {
  /** Pixels scrolled in one direction before the toolbar reacts, so a jittery finger does not flicker it. */
  private static readonly THRESHOLD = 12;

  readonly appHideOnScroll = input<number>(40);
  /** While true the toolbar stays put (e.g. it holds the actions for the selected rows). */
  readonly hideOnScrollDisabled = input<boolean>(false);

  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef).nativeElement;
  private readonly bp = inject(BreakpointService);
  private readonly renderer = inject(Renderer2);
  private readonly zone = inject(NgZone);

  private lastTop = new WeakMap<EventTarget, number>();
  private hidden = false;

  constructor() {
    effect(() => this.renderer.setStyle(this.host, '--hide-on-scroll-offset', `${this.appHideOnScroll()}px`));
    // A resize to a wide layout, or a switch to "keep visible", must not leave the toolbar collapsed.
    effect(() => {
      if (!this.bp.isCompact() || this.hideOnScrollDisabled()) {
        this.setHidden(false);
      }
    });

    const onScroll = (event: Event) => this.onScroll(event);
    // Outside the zone: scroll fires often and only a class changes, so no change detection is needed.
    this.zone.runOutsideAngular(() => document.addEventListener('scroll', onScroll, { capture: true, passive: true }));
    inject(DestroyRef).onDestroy(() => document.removeEventListener('scroll', onScroll, { capture: true }));
  }

  private onScroll(event: Event): void {
    const target = event.target;
    if (
      !this.bp.isCompact() ||
      this.hideOnScrollDisabled() ||
      !(target instanceof HTMLElement) ||
      target.contains(this.host) ||
      this.host.contains(target)
    ) {
      // Not a list below the toolbar: the page itself (contains the host) or a scroller inside the toolbar, such as
      // the filter chips or a filter picker.
      return;
    }
    const top = target.scrollTop;
    const delta = top - (this.lastTop.get(target) ?? 0);
    if (top <= this.appHideOnScroll()) {
      this.setHidden(false);
    } else if (delta > HideOnScrollDirective.THRESHOLD) {
      this.setHidden(true);
    } else if (delta < -HideOnScrollDirective.THRESHOLD) {
      this.setHidden(false);
    } else {
      return; // keep the anchor until the movement is big enough
    }
    this.lastTop.set(target, top);
  }

  private setHidden(hidden: boolean): void {
    if (hidden !== this.hidden) {
      this.hidden = hidden;
      // The toolbar can be taller than its first row (active filter chips): collapse by its real height.
      if (hidden) this.renderer.setStyle(this.host, '--hide-on-scroll-offset', `${this.host.offsetHeight}px`);
      this.host.classList.toggle('scroll-hidden', hidden);
    }
  }
}
